import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button, Grid, MenuItem, Alert, CircularProgress, Divider
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useOffline } from '../../../hooks/useOffline';
import OfflineBanner from '../../../components/common/OfflineBanner';
import WorkoutStructureBuilder from '../../workouts/components/WorkoutStructureBuilder';

// Import Types from the shared model file
import type {
  WorkoutCourseCreate,
  CourseStep,
  HRZoneSpec,
  HRRangeSpec,
  PaceZoneSpec,
  PaceRangeSpec,
  PowerZoneSpec,
  PowerRangeSpec,
  Movement,
  Set
} from '../../../store/api/models/workoutModels';

import {
  useGetWorkoutCourseQuery,
  useUpdateWorkoutCourseMutation
} from '../../../store/api/baseApi';

interface TemplateFormData {
  name: string;
  description: string;
  sportType: string;
}

const sportTypes = [
  { value: 'run', label: 'Run' },
  { value: 'ride', label: 'Ride' },
  { value: 'swim', label: 'Swim' },
];

const validationSchema = yup.object().shape({
  name: yup.string().required('Template name is required').min(3, 'Name must be at least 3 characters'),
  description: yup.string().required('Description is required').min(10, 'Description must be at least 10 characters'),
  sportType: yup.string().required('Sport type is required').oneOf(['run', 'ride', 'swim'], 'Invalid sport type'),
});

// Helper to remove UI-only properties (like the synthetic 'id')
// We cast to 'any' here because 'id' is added at runtime by the Builder but isn't in the CourseStep interface.
const sanitizeCourseSteps = (steps: CourseStep[]): CourseStep[] => {
  return (steps as any[]).map(({ id, ...rest }: any) => {
    // If it's a set, we also need to sanitize the movements inside it
    if (rest.step_type === 'set') {
      return {
        ...rest,
        content: {
          ...(rest.content as Set),
          movements: (rest.content as Set).movements.map((m: any) => {
            // Strip ID from movements if present
            const { id: mId, ...mRest } = m;
            return mRest;
          })
        }
      };
    }
    return rest;
  });
};

const UpdateTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const { isOffline } = useOffline();

  // Store the builder state
  const [workoutStructure, setWorkoutStructure] = useState<CourseStep[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  // API Hooks
  // CORRECT: Using useGetWorkoutCourseQuery to fetch the Template/Course definition
  const { data: existingTemplate, isLoading: isLoadingTemplate, error: fetchError } = useGetWorkoutCourseQuery(
    Number(templateId!),
    { skip: !templateId }
  );
  
  const [updateCourse] = useUpdateWorkoutCourseMutation(); 

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<TemplateFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: '',
      description: '',
      sportType: 'run',
    },
  });

  // Initialize form and structure when template loads
  useEffect(() => {
    if (existingTemplate) {
      reset({
        name: existingTemplate.name,
        description: existingTemplate.description || '',
        sportType: existingTemplate.sport_type || 'run',
      });
      // Initialize the structure state directly from the API response
      // We clone it to ensure we aren't mutating the RTK Query cache directly in the UI
      setWorkoutStructure(JSON.parse(JSON.stringify(existingTemplate.structure)));
    }
  }, [existingTemplate, reset]);

  const handleStructureChange = (newStructure: CourseStep[]) => {
    setWorkoutStructure(newStructure);
  };

  const onSubmit = async (data: TemplateFormData) => {
    setSaveError(null);

    if (workoutStructure.length === 0) {
      setSaveError('Workout structure cannot be empty. Please add at least one step.');
      return;
    }

    // --- Validation Logic ---
    // Ensure structure meets logic constraints (e.g. Min < Max for ranges)
    for (const step of workoutStructure) {
       if (step.step_type === 'movement') {
          const mov = step.content as Movement;

          // Validate HR range
          if (mov.intensity_focus === 'hr' && mov.intensity_value) {
             const hr = mov.intensity_value as HRRangeSpec | HRZoneSpec;
             if ('min_hr' in hr && 'max_hr' in hr) {
               if (hr.min_hr >= hr.max_hr) {
                 setSaveError(`Step ${step.step_order + 1}: Min HR must be less than Max HR.`);
                 return;
               }
             }
          }

          // Validate Pace range
          if (mov.intensity_focus === 'pace' && mov.intensity_value) {
             const pace = mov.intensity_value as PaceRangeSpec | PaceZoneSpec;
             if ('min_pace' in pace && 'max_pace' in pace) {
               if (pace.min_pace >= pace.max_pace) {
                 setSaveError(`Step ${step.step_order + 1}: Min pace must be less than Max pace.`);
                 return;
               }
             }
          }

          // Validate Power range
          if (mov.intensity_focus === 'power' && mov.intensity_value) {
             const power = mov.intensity_value as PowerRangeSpec | PowerZoneSpec;
             if ('min_watts' in power && 'max_watts' in power) {
               if (power.min_watts >= power.max_watts) {
                 setSaveError(`Step ${step.step_order + 1}: Min power must be less than Max power.`);
                 return;
               }
             }
          }
       }

       if (step.step_type === 'set') {
          const set = step.content as Set;
          for(const mov of set.movements) {
             // Validate HR range
             if (mov.intensity_focus === 'hr' && mov.intensity_value) {
               const hr = mov.intensity_value as HRRangeSpec | HRZoneSpec;
               if ('min_hr' in hr && 'max_hr' in hr) {
                 if (hr.min_hr >= hr.max_hr) {
                   setSaveError(`Set ${step.step_order + 1}: Min HR must be less than Max HR.`);
                   return;
                 }
               }
             }

             // Validate Pace range
             if (mov.intensity_focus === 'pace' && mov.intensity_value) {
               const pace = mov.intensity_value as PaceRangeSpec | PaceZoneSpec;
               if ('min_pace' in pace && 'max_pace' in pace) {
                 if (pace.min_pace >= pace.max_pace) {
                   setSaveError(`Set ${step.step_order + 1}: Min pace must be less than Max pace.`);
                   return;
                 }
               }
             }

             // Validate Power range
             if (mov.intensity_focus === 'power' && mov.intensity_value) {
               const power = mov.intensity_value as PowerRangeSpec | PowerZoneSpec;
               if ('min_watts' in power && 'max_watts' in power) {
                 if (power.min_watts >= power.max_watts) {
                   setSaveError(`Set ${step.step_order + 1}: Min power must be less than Max power.`);
                   return;
                 }
               }
             }
          }
       }
    }

    // --- Payload Construction ---
    const templateData: WorkoutCourseCreate = {
      name: data.name,
      sport_type: data.sportType as any,
      description: data.description,
      structure: sanitizeCourseSteps(workoutStructure), // Sanitize before sending
    };

    try {
      await updateCourse({ course_id: Number(templateId), data: templateData }).unwrap();
      navigate('/trainer/templates');
    } catch (error: any) {
      setSaveError(error?.data?.message || error?.error || 'Failed to update template. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate('/trainer/templates');
  };

  if (isLoadingTemplate) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (fetchError) {
     return (
       <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Alert severity="error">Failed to load template data. It may have been deleted or you do not have permission to view it.</Alert>
          <Button sx={{ mt: 2 }} onClick={handleCancel}>Go Back</Button>
       </Container>
     )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <OfflineBanner />

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Edit Workout Template
        </Typography>
        {saveError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={3}>
            {/* Metadata Section */}
            <Grid item xs={12}>
               <Controller name="name" control={control} render={({ field }) => (
                  <TextField {...field} label="Template Name" fullWidth required error={Boolean(errors.name)} helperText={errors.name?.message} />
               )} />
            </Grid>
            <Grid item xs={12} md={4}>
               <Controller name="sportType" control={control} render={({ field }) => (
                  <TextField {...field} select label="Sport Type" fullWidth required error={Boolean(errors.sportType)} helperText={errors.sportType?.message}>
                    {sportTypes.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                  </TextField>
               )} />
            </Grid>
            <Grid item xs={12}>
               <Controller name="description" control={control} render={({ field }) => (
                  <TextField {...field} label="Description" fullWidth required multiline rows={3} error={Boolean(errors.description)} helperText={errors.description?.message} />
               )} />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Structure Section */}
            <Grid item xs={12}>
               <Typography variant="h6">Workout Structure</Typography>
               <WorkoutStructureBuilder
                 initialStructure={existingTemplate?.structure}
                 onChange={handleStructureChange}
               />
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            <Grid item xs={12}>
               <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
                  <Button type="submit" variant="contained" startIcon={<SaveIcon />}>Save Changes</Button>
               </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default UpdateTemplatePage;